import { LoggedUser } from 'modules/users/models/LoggedUser'
import { UserDTO } from 'modules/users/models/User'

// PERMISOS DE USUARIOS
export const PERM_CREATE_PATIENT = 'users:create:patient'
export const PERM_CREATE_FAMILIAR = 'users:create:familiar'
export const PERM_CREATE_USER = 'users:create:any'
export const PERM_VIEW_ANY_USER = 'users:view:any'
export const PERM_EDIT_ANY_USER = 'users:edit:any'
export const PERM_EDIT_OWN_DATA = 'users:edit:own'
export const PERM_DELETE_USER = 'users:delete:any'
export const PERM_MANAGE_ROLES = 'users:manage:roles'
export const PERM_CHANGE_OWN_PASSWORD = 'users:change-password:own'

// PERMISOS DE HOME
export const PERM_VIEW_HOME = 'home:view'

// PERMISOS DE PACIENTES
export const PERM_VIEW_PATIENTS = 'patients:view:list'
export const PERM_VIEW_PATIENT_DETAILS = 'patients:view:details'
export const PERM_EDIT_PATIENT_DATA = 'patients:edit:data'
export const PERM_VIEW_PATIENT_STATS = 'patients:view:stats'
export const PERM_MANAGE_PATIENT_CIRCLE = 'patients:manage:circle'

// PERMISOS DE FORMULARIOS
export const PERM_CREATE_FORMS = 'forms:create'
export const PERM_ANSWER_FORMS = 'forms:answer'
export const PERM_VIEW_FORMS = 'forms:view'

// PERMISOS DE PROTOCOLOS (#3188)
export const PERM_VIEW_PROTOCOLS = 'protocols:view'
export const PERM_MANAGE_PROTOCOLS = 'protocols:manage'
export const PERM_APPROVE_PROTOCOLS = 'protocols:approve'
export const PERM_ASSIGN_PROTOCOLS = 'protocols:assign'

// PERMISOS DE ESTADÍSTICAS
export const PERM_VIEW_OWN_STATS = 'stats:view:own'
export const PERM_VIEW_ANY_STATS = 'stats:view:any'

// PERMISOS DE ARCHIVOS
export const PERM_UPLOAD_FILES = 'files:upload'
export const PERM_VIEW_FILES = 'files:view'
export const PERM_DELETE_FILES = 'files:delete'
export const PERM_SHARE_FILES = 'files:share'

// PERMISOS DE WEARABLES
export const PERM_LINK_WEARABLE = 'wearables:link'
export const PERM_VIEW_WEARABLE = 'wearables:view'

// PERMISOS DE NOTIFICACIONES

// FUNCIONES BASE
export const hasPermission = (
  user: UserDTO | LoggedUser | undefined | null,
  permission: string
): boolean => {
  if (!user?.permissions) return false
  return user.permissions.includes(permission)
}

export const hasAnyPermission = (
  user: UserDTO | LoggedUser | undefined | null,
  permissions: string[]
): boolean => {
  if (!user?.permissions) return false
  return permissions.some((perm) => user.permissions?.includes(perm))
}

export const hasAllPermissions = (
  user: UserDTO | LoggedUser | undefined | null,
  permissions: string[]
): boolean => {
  if (!user?.permissions) return false
  return permissions.every((perm) => user.permissions?.includes(perm))
}

// PERMISOS DE USUARIO
export const canEditOwnData = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_OWN_DATA)
}

export const canChangeOwnPassword = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CHANGE_OWN_PASSWORD)
}

export const canCreateUser = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_USER)
}

export const canCreateFamiliar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_FAMILIAR)
}

export const canViewAnyUser = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_ANY_USER)
}

export const canEditAnyUser = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_ANY_USER)
}

export const canDeleteUser = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_DELETE_USER)
}

export const canManageRoles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_MANAGE_ROLES)
}

// PERMISOS DE MENÚ
export const canAccessHome = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_HOME)
}

export const canViewPatients = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_PATIENTS)
}

export const canViewStatistics = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [PERM_VIEW_OWN_STATS, PERM_VIEW_ANY_STATS])
}

export const canAccessStatisticsMenu = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_OWN_STATS)
}

export const canCreatePatient = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_PATIENT)
}

export const canEditPatientData = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_PATIENT_DATA)
}

export const canManagePatientCircle = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_MANAGE_PATIENT_CIRCLE)
}

export const canViewPatientDetails = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_PATIENT_DETAILS)
}

export const canViewPatientStats = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_PATIENT_STATS)
}

export const canLinkWearable = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_LINK_WEARABLE)
}

export const canViewWearable = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_WEARABLE)
}

// PERMISOS DE FORMULARIOS
export const canAccessForms = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [PERM_ANSWER_FORMS, PERM_CREATE_FORMS, PERM_VIEW_FORMS])
}

export const canCreateForms = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_FORMS)
}

export const canAnswerForms = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_ANSWER_FORMS)
}

// PERMISOS DE ARCHIVOS
export const canUploadFiles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_UPLOAD_FILES)
}

export const canViewFiles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_FILES)
}

export const canDeleteFiles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_DELETE_FILES)
}

export const canShareFiles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_SHARE_FILES)
}

export const canAccessResources = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_FILES)
}

// PERMISOS DE ESTADÍSTICAS
export const canViewOwnStats = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_OWN_STATS)
}

export const canViewAnyStats = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_ANY_STATS)
}

// PERMISOS DE PROTOCOLOS (#3188)
export const canViewProtocols = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_PROTOCOLS)
}

export const canManageProtocols = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_MANAGE_PROTOCOLS)
}

export const canApproveProtocols = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_APPROVE_PROTOCOLS)
}

export const canAssignProtocols = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_ASSIGN_PROTOCOLS)
}
